/*
 * Espayo — required helmet liner picker
 * Forces a liner choice before a helmet can be added to the basket, and adds
 * the helmet and its liner to the cart as a single action.
 *
 * Depends on: snippets/espayo-liner-picker.liquid
 */
(function () {
  'use strict';

  var CM = /(\d{2})\s*c?m/g;

  /* Pull the cm range out of a helmet size string.
     Handles "Medium (56cm-58cm)", "Medium 57cm-59cm" and the "Small 51cm-56m" typo. */
  function parseRange(label) {
    var part = sizePart(label);
    if (!part) return null;
    var nums = [], m;
    CM.lastIndex = 0;
    while ((m = CM.exec(part)) !== null) nums.push(parseInt(m[1], 10));
    if (nums.length < 2) return null;
    return { from: Math.min(nums[0], nums[1]), to: Math.max(nums[0], nums[1]) };
  }

  /* Leading cm value of a liner size: "54cm", "57cm (M)" */
  function parseLinerCm(label) {
    var m = /(\d{2})\s*cm/.exec(label || '');
    return m ? parseInt(m[1], 10) : null;
  }

  /* Samshield liner sizes carry the shell they belong to: "57cm (M)".
     The same cm exists under two shells, so cm alone is not enough. */
  function parseLinerShell(label) {
    var m = /\(\s*(XL|L|M|S)\s*\)/i.exec(label || '');
    return m ? m[1].toUpperCase() : null;
  }

  /* The variant title carries the colour too ("Black / X Large (61cm-64cm)"),
     so work from the part after the last slash. */
  function sizePart(label) {
    if (!label) return '';
    var parts = String(label).split('/');
    return parts[parts.length - 1].trim();
  }

  /* Shell letter from a helmet variant title: "Black / X Large (61cm-64cm)" -> "XL" */
  function parseHelmetShell(label) {
    var l = sizePart(label).toLowerCase();
    if (!l) return null;
    if (l.indexOf('x large') === 0 || l.indexOf('xlarge') === 0 || l.indexOf('x-large') === 0) return 'XL';
    if (l.indexOf('large') === 0) return 'L';
    if (l.indexOf('medium') === 0) return 'M';
    if (l.indexOf('small') === 0) return 'S';
    return null;
  }

  function readJSON(el, attr, fallback) {
    try { return JSON.parse(el.getAttribute(attr)); } catch (e) { return fallback; }
  }

  function LinerPicker(root) {
    this.root = root;
    this.liners = readJSON(root, 'data-liners', []) || [];
    this.helmetSizes = readJSON(root, 'data-helmet-sizes', {}) || {};
    this.helmetTitle = root.getAttribute('data-helmet-title') || '';
    this.fitWrap = root.querySelector('[data-espayo-fit]');
    this.fitOptions = root.querySelector('[data-espayo-fit-options]');
    this.sizeOptions = root.querySelector('[data-espayo-size-options]');
    this.emptyMsg = root.querySelector('[data-espayo-empty]');
    this.errorMsg = root.querySelector('[data-espayo-error]');
    this.form = this.findForms()[0];
    this.selectedFit = this.liners.length ? this.liners[0].handle : null;
    this.selectedVariant = null;

    if (!this.liners.length || !this.form) return;


    this.suppressExpressCheckout();
    this.renderFit();
    this.renderSizes();
    this.watchHelmetVariant();
    this.interceptSubmit();
  }

  /* This theme renders blocks OUTSIDE the product form, and the page carries more
     than one add-to-cart form (product form, sticky bar, quick-buy). Pick every form
     whose variant input holds one of THIS helmet's variants, rather than trusting
     DOM position. */
  LinerPicker.prototype.findForms = function () {
    var sizes = this.helmetSizes;
    var all = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
    var mine = all.filter(function (f) {
      var input = f.querySelector('[name="id"]');
      return input && Object.prototype.hasOwnProperty.call(sizes, String(input.value));
    });
    /* Visible forms first, so the one the customer is looking at drives the sizes. */
    mine.sort(function (a, b) {
      var av = a.offsetParent !== null ? 0 : 1;
      var bv = b.offsetParent !== null ? 0 : 1;
      return av - bv;
    });
    return mine;
  };

  /* Express checkout (Shop Pay / Buy it now) posts straight to checkout and would
     skip the liner entirely, so it is removed on helmet pages. */
  LinerPicker.prototype.suppressExpressCheckout = function () {
    document.documentElement.classList.add('espayo-no-express');
  };

  LinerPicker.prototype.activeForm = function () {
    var forms = this.findForms();
    return forms[0] || this.form;
  };

  LinerPicker.prototype.currentHelmetVariantId = function () {
    var input = this.activeForm().querySelector('[name="id"]');
    return input ? String(input.value) : null;
  };

  LinerPicker.prototype.currentRange = function () {
    var id = this.currentHelmetVariantId();
    var label = id && this.helmetSizes[id];
    return parseRange(label);
  };

  LinerPicker.prototype.currentShell = function () {
    var id = this.currentHelmetVariantId();
    var label = id && this.helmetSizes[id];
    return parseHelmetShell(label);
  };

  LinerPicker.prototype.renderFit = function () {
    if (this.liners.length < 2) return;
    var self = this;
    this.fitOptions.innerHTML = '';
    this.liners.forEach(function (liner, i) {
      var id = 'espayo-fit-' + i;
      var label = document.createElement('label');
      label.className = 'espayo-liner__opt';
      label.innerHTML =
        '<input type="radio" name="espayo-liner-fit" id="' + id + '" value="' + liner.handle + '"' +
        (i === 0 ? ' checked' : '') + '>' +
        '<span>' + liner.fitLabel + '</span>';
      label.querySelector('input').addEventListener('change', function () {
        self.selectedFit = liner.handle;
        self.renderSizes();
      });
      self.fitOptions.appendChild(label);
    });
    this.fitWrap.hidden = false;
  };

  LinerPicker.prototype.renderSizes = function () {
    var self = this;
    var liner = this.liners.filter(function (l) { return l.handle === self.selectedFit; })[0];
    var range = this.currentRange();

    this.selectedVariant = null;
    this.sizeOptions.innerHTML = '';
    this.clearError();

    if (!liner) { this.emptyMsg.hidden = false; return; }

    var shell = this.currentShell();

    var inRange = liner.variants.filter(function (v) {
      var cm = parseLinerCm(v.size);
      if (cm === null) return false;

      /* Where the liner names a shell (Samshield), it must match the shell the
         customer picked. Without this a Medium shell also offers "57cm (L)". */
      var linerShell = parseLinerShell(v.size);
      if (linerShell && shell && linerShell !== shell) return false;

      if (!range) return true;              /* no parsable helmet range: offer everything */
      return cm >= range.from && cm <= range.to;
    });

    /* Sort by cm so the ladder reads 51, 52, 53 ... rather than catalogue order. */
    inRange.sort(function (a, b) { return parseLinerCm(a.size) - parseLinerCm(b.size); });

    /* Liners are always sellable: every liner variant is set to continue selling
       when out of stock, and a size is never shown crossed out or disabled.
       The empty message only appears if the shell has no liner sizes at all. */
    this.emptyMsg.hidden = inRange.length > 0;

    inRange.forEach(function (v, i) {
      var id = 'espayo-size-' + i;
      var label = document.createElement('label');
      label.className = 'espayo-liner__opt';
      label.innerHTML =
        '<input type="radio" name="espayo-liner-size" id="' + id + '" value="' + v.id + '">' +
        '<span>' + v.size + '</span>';
      label.querySelector('input').addEventListener('change', function () {
        self.selectedVariant = v;
        self.clearError();
      });
      self.sizeOptions.appendChild(label);
    });
    /* Deliberately nothing is pre-selected — the customer must choose. */
  };

  /* The helmet size can change after load, so re-filter whenever it does. */
  LinerPicker.prototype.watchHelmetVariant = function () {
    var self = this;
    var last = this.currentHelmetVariantId();

    var check = function () {
      var now = self.currentHelmetVariantId();
      if (now !== last) { last = now; self.renderSizes(); }
    };

    ['change', 'variant:change', 'on:variant:change'].forEach(function (evt) {
      document.addEventListener(evt, check, true);
    });
    /* The theme re-renders the product form on variant change, so observe the
       whole document rather than an input that gets replaced. */
    new MutationObserver(check).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    setInterval(check, 400);
  };

  LinerPicker.prototype.showError = function (msg) {
    if (msg) this.errorMsg.textContent = msg;
    this.errorMsg.hidden = false;
    this.root.classList.add('espayo-liner--invalid');
    this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  LinerPicker.prototype.clearError = function () {
    this.errorMsg.hidden = true;
    this.root.classList.remove('espayo-liner--invalid');
  };

  /* Delegated on the document in the capture phase. The theme replaces the product
     form and its button when the variant changes, so anything bound directly to
     those elements goes stale; this does not. */
  LinerPicker.prototype.interceptSubmit = function () {
    var self = this;

    var formFor = function (node) {
      if (!node || !node.closest) return null;
      var form = node.closest('form[action*="/cart/add"]');
      if (!form) return null;
      var input = form.querySelector('[name="id"]');
      if (!input) return null;
      return Object.prototype.hasOwnProperty.call(self.helmetSizes, String(input.value)) ? form : null;
    };

    var act = function (e, form) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!self.selectedVariant) { self.showError(); return false; }
      self.addBoth(form);
      return false;
    };

    document.addEventListener('submit', function (e) {
      var form = formFor(e.target);
      if (form) act(e, form);
    }, true);

    document.addEventListener('click', function (e) {
      var node = e.target;
      if (!node || !node.closest) return;
      var btn = node.closest('[type="submit"], button[name="add"]');
      if (!btn) return;
      var form = formFor(btn);
      if (form) act(e, form);
    }, true);
  };

  LinerPicker.prototype.addBoth = function (sourceForm) {
    var self = this;
    var form = sourceForm || this.activeForm();
    var idInput = form.querySelector('[name="id"]');
    var helmetId = idInput ? idInput.value : this.currentHelmetVariantId();
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var button = form.querySelector('[type="submit"]');

    if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [
          { id: Number(helmetId), quantity: qty },
          {
            id: Number(self.selectedVariant.id),
            quantity: qty,
            properties: { 'Liner for': self.helmetTitle }
          }
        ]
      })
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body && res.body.description || 'Could not add to basket');
        self.refreshCart();
      })
      .catch(function (err) {
        self.showError(err.message || 'Sorry, something went wrong adding to your basket.');
      })
      .finally(function () {
        if (button) { button.removeAttribute('aria-busy'); button.disabled = false; }
      });
  };

  /* Symmetry's cart drawer listens for these two document events. Refresh first so
     the drawer has the new lines, then open it. */
  LinerPicker.prototype.refreshCart = function () {
    var drawer = document.querySelector('cart-drawer');

    try {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    } catch (e) { /* ignore */ }

    if (!drawer) {
      /* No drawer in this theme setup (cart page mode) — go to the cart. */
      window.location.href = '/cart';
      return;
    }

    setTimeout(function () {
      try {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      } catch (e) {
        window.location.href = '/cart';
      }
    }, 350);
  };

  /* ---- Backorder notice ------------------------------------------------
     Independent of the picker so it still works on a helmet with no mapped
     liner. Shows when the selected variant is set to continue selling and has
     no stock. */
  function BackorderNotice(root) {
    this.root = root;
    this.states = readJSON(root, 'data-states', {}) || {};
    var self = this;
    var last = null;

    var currentId = function () {
      var forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
      var mine = forms.filter(function (f) {
        var input = f.querySelector('[name="id"]');
        return input && Object.prototype.hasOwnProperty.call(self.states, String(input.value));
      });
      mine.sort(function (a, b) {
        return (a.offsetParent !== null ? 0 : 1) - (b.offsetParent !== null ? 0 : 1);
      });
      var input = mine[0] && mine[0].querySelector('[name="id"]');
      return input ? String(input.value) : null;
    };

    var update = function () {
      var id = currentId();
      if (id === last) return;
      last = id;
      self.root.hidden = !(id && self.states[id] === true);
    };

    update();
    ['change', 'variant:change', 'on:variant:change'].forEach(function (evt) {
      document.addEventListener(evt, update, true);
    });
    new MutationObserver(update).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    setInterval(update, 400);
  }

  function init() {
    document.querySelectorAll('[data-espayo-liner]').forEach(function (el) {
      if (el.dataset.espayoReady) return;
      el.dataset.espayoReady = '1';
      new LinerPicker(el);
    });
    document.querySelectorAll('[data-espayo-backorder]').forEach(function (el) {
      if (el.dataset.espayoReady) return;
      el.dataset.espayoReady = '1';
      new BackorderNotice(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* Re-init if the theme re-renders the product form (variant change, quick view) */
  document.addEventListener('shopify:section:load', init);
})();
